import { computed, inject, Injectable } from '@angular/core';

import { patchState, signalState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';

import { delay, pipe, switchMap, tap, concatMap } from 'rxjs';

import {
  DashboardWaitingOnYouRequestParamsV2,
  OutcomeDocumentsCheckParamsV2,
  WAITING_ON_YOU_SIGNAL_STATE_V2,
  WaitingOnYouSeeDetailsCheckParamsV2,
  WaitingOnYouTodoCheckParamsV2,
} from '../../types';

import {
  determineErrorType,
  WAITING_ON_YOU_V2_DEFAULT_SIGNAL_STATE,
  WAITING_ON_YOU_V2_ERROR_TYPE_ENUM,
} from '../../constants';
import { DashboardService } from '../../services';

const ON_YOU_SIGNAL_STATE_V2 = signalState<WAITING_ON_YOU_SIGNAL_STATE_V2>({
  ...WAITING_ON_YOU_V2_DEFAULT_SIGNAL_STATE,
});

@Injectable()
export class WaitingOnYouV2SignalState {
  private readonly _onYouSignalStateV2 = ON_YOU_SIGNAL_STATE_V2;
  private readonly _dashboardService = inject(DashboardService);

  readonly $isLoading = computed(() => this._onYouSignalStateV2.isLoading());
  readonly $error = computed(() => this._onYouSignalStateV2.error());

  readonly $totalCount = computed(
    () => this._onYouSignalStateV2.data()?.count ?? 0,
  );

  readonly $waitingOnYouData = computed(() => this._onYouSignalStateV2.data());
  readonly $checkTodoAccessResponse = computed(() =>
    this._onYouSignalStateV2.checkTodoAccessResponse(),
  );
  readonly $checkOutcomeAccessResponse = computed(() =>
    this._onYouSignalStateV2.checkOutcomeAccessResponse(),
  );
  readonly $checkSeeDetailsAccessResponse = computed(() =>
    this._onYouSignalStateV2.checkSeeDetailsAccessResponse(),
  );

  readonly $checkTodoAccessResponseLoading = computed(() =>
    this._onYouSignalStateV2.checkTodoAccessResponseLoading(),
  );
  readonly $checkOutcomeAccessResponseLoading = computed(() =>
    this._onYouSignalStateV2.checkOutcomeAccessResponseLoading(),
  );
  readonly $checkSeeDetailsAccessResponseLoading = computed(() =>
    this._onYouSignalStateV2.checkSeeDetailsAccessResponseLoading(),
  );

  readonly fetchWaitingOnYou = rxMethod<{
    payload: DashboardWaitingOnYouRequestParamsV2;
    useMock?: boolean;
  }>(
    pipe(
      tap(() =>
        patchState(this._onYouSignalStateV2, { isLoading: true, error: null }),
      ),
      concatMap(({ payload, useMock }) =>
        this._dashboardService.getWaitingOnYouItemsV2(payload, useMock).pipe(
          delay(250),
          tapResponse({
            error: (error: Error) =>
              patchState(this._onYouSignalStateV2, {
                isLoading: false,
                error: determineErrorType(
                  WAITING_ON_YOU_V2_ERROR_TYPE_ENUM.DATA,
                  error,
                ),
              }),
            next: (response) =>
              patchState(this._onYouSignalStateV2, {
                isLoading: false,
                error: null,
                data: response,
              }),
          }),
        ),
      ),
    ),
  );

  readonly checkWaitingOnYouTodoAccess = rxMethod<{
    payload: WaitingOnYouTodoCheckParamsV2;
  }>(
    pipe(
      tap(() =>
        patchState(this._onYouSignalStateV2, {
          error: null,
          checkTodoAccessResponseLoading: true,
          checkTodoAccessResponse: null,
        }),
      ),
      switchMap(({ payload }) =>
        this._dashboardService.checkWaitingOnYouTodoV2(payload).pipe(
          tapResponse({
            error: (error: Error) =>
              patchState(this._onYouSignalStateV2, {
                error: determineErrorType(
                  WAITING_ON_YOU_V2_ERROR_TYPE_ENUM.TODO_ACCESS,
                  error,
                ),
                isLoading: false,
                checkTodoAccessResponseLoading: false,
              }),
            next: (response) =>
              patchState(this._onYouSignalStateV2, {
                error: null,
                checkTodoAccessResponseLoading: false,
                checkTodoAccessResponse: response,
              }),
          }),
        ),
      ),
    ),
  );

  readonly checkWaitingOnYouOutcomeDocumentAccess = rxMethod<{
    payload: OutcomeDocumentsCheckParamsV2;
  }>(
    pipe(
      tap(() =>
        patchState(this._onYouSignalStateV2, {
          error: null,
          checkOutcomeAccessResponseLoading: true,
          checkOutcomeAccessResponse: null,
        }),
      ),
      switchMap(({ payload }) =>
        this._dashboardService.checkOutcomeDocumentsV2(payload).pipe(
          tapResponse({
            error: (error: Error) =>
              patchState(this._onYouSignalStateV2, {
                error: determineErrorType(
                  WAITING_ON_YOU_V2_ERROR_TYPE_ENUM.OUTCOME_ACCESS,
                  error,
                ),
                checkOutcomeAccessResponseLoading: false,
              }),
            next: (response) =>
              patchState(this._onYouSignalStateV2, {
                error: null,
                checkOutcomeAccessResponseLoading: false,
                checkOutcomeAccessResponse: response,
              }),
          }),
        ),
      ),
    ),
  );

  readonly checkWaitingOnYouSeeDetailsAccess = rxMethod<{
    payload: WaitingOnYouSeeDetailsCheckParamsV2;
  }>(
    pipe(
      tap(() =>
        patchState(this._onYouSignalStateV2, {
          error: null,
          checkSeeDetailsAccessResponseLoading: true,
          checkSeeDetailsAccessResponse: null,
        }),
      ),
      switchMap(({ payload }) =>
        this._dashboardService.checkWaitingOnYouSeeDetailsV2(payload).pipe(
          tapResponse({
            error: (error: Error) =>
              patchState(this._onYouSignalStateV2, {
                error: determineErrorType(
                  WAITING_ON_YOU_V2_ERROR_TYPE_ENUM.SEE_DETAILS_ACCESS,
                  error,
                ),
                checkSeeDetailsAccessResponseLoading: false,
              }),
            next: (response) =>
              patchState(this._onYouSignalStateV2, {
                error: null,
                checkSeeDetailsAccessResponseLoading: false,
                checkSeeDetailsAccessResponse: response,
              }),
          }),
        ),
      ),
    ),
  );

  reset(): void {
    patchState(this._onYouSignalStateV2, {
      ...WAITING_ON_YOU_V2_DEFAULT_SIGNAL_STATE,
    });
  }

  loadErrorState(): void {
    patchState(this._onYouSignalStateV2, {
      isLoading: false,
      error: determineErrorType(
        WAITING_ON_YOU_V2_ERROR_TYPE_ENUM.DATA,
        new Error('Error State Loading'),
      ),
    });
  }

  loadEmptyState(): void {
    patchState(this._onYouSignalStateV2, {
      isLoading: false,
      error: null,
      data: null,
    });
  }

  loadLoadingState(): void {
    patchState(this._onYouSignalStateV2, {
      isLoading: true,
      error: null,
      data: null,
    });
  }
}
